// frontend/src/pages/Facilitation/PendingFacilitationDetail.jsx
// Vista de facilitación para cotización aceptada sin OC

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
} from 'reactstrap';
import { quotesAPI } from '../../services/api';
import { parseDate } from '../../utils/dates';

function PendingFacilitationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuote();
  }, [id]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await quotesAPI.getById(id);
      setQuote(response.data);
    } catch (error) {
      console.error('Error al cargar cotización:', error);
      navigate('/admin/facilitation');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando información...</p>
          </div>
        </Container>
      </>
    );
  }

  if (!quote) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">Cotización no encontrada</h4>
              <Button color="primary" className="mt-3" onClick={() => navigate('/admin/facilitation')}>
                Volver a Facilitación
              </Button>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  const fac = quote.manual_acceptance_data || {};

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100" style={{ zIndex: 0 }}></div>

      <Container fluid className="py-4" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Row className="mb-3">
          <Col xs="12" md="6" className="d-flex align-items-center">
            <Button
              color="white"
              size="sm"
              className="btn-icon shadow-sm"
              onClick={() => navigate('/admin/facilitation')}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-bold-left"></i>
              </span>
              <span className="btn-inner--text ms-1">Volver a Facilitación</span>
            </Button>
          </Col>
          <Col xs="12" md="6" className="d-flex justify-content-md-end">
            <Button color="primary" size="sm" className="btn-icon shadow-sm" onClick={handlePrint}>
              <span className="btn-inner--icon">
                <i className="ni ni-cloud-download-95"></i>
              </span>
              <span className="btn-inner--text ms-1">Imprimir / Descargar PDF</span>
            </Button>
          </Col>
        </Row>

        <Row className="printable-content">
          {/* Contenido principal */}
          <Col lg="8">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="8">
                    <h5 className="mb-0 text-white">
                      <i className="ni ni-hat-3 me-2"></i>
                      {quote.proposal_number}
                    </h5>
                    <p className="text-sm text-white opacity-8 mb-0">
                      <i className="ni ni-calendar-grid-58 me-1"></i>
                      Creada el {parseDate(quote.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </Col>
                  <Col md="4" className="text-end">
                    <Badge className="badge-lg bg-white text-warning shadow-sm">
                      <i className="ni ni-time-alarm me-1"></i>
                      Pendiente OC
                    </Badge>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="p-4">
                {/* 1. INFORMACIÓN DEL CLIENTE */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-building me-2"></i>
                    Información del Cliente
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Cliente:</strong>
                        <span className="text-muted ms-2">{quote.client_name || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Propuesta:</strong>
                        <span className="text-muted ms-2">{quote.proposal_number}</span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Contacto:</strong>
                        <span className="text-muted ms-2">{quote.contact_name || fac.contact_name || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Email:</strong>
                        <span className="text-muted ms-2">{quote.contact_email || fac.contact_email || 'N/A'}</span>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* 2. CONFIRMACIÓN */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-check-bold me-2"></i>
                    Confirmación
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Medio:</strong>
                        <span className="text-muted ms-2">{fac.confirmation_method || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Fecha de Confirmación:</strong>
                        <span className="text-muted ms-2">
                          {fac.confirmation_date
                            ? parseDate(fac.confirmation_date).toLocaleDateString('es-MX')
                            : 'N/A'}
                        </span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Referencia:</strong>
                        <span className="text-muted ms-2">{fac.confirmation_reference || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Vendedor:</strong>
                        <Badge color="primary" className="ms-2">{quote.seller_name || 'N/A'}</Badge>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* 3. DETALLES DEL SERVICIO */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-books me-2"></i>
                    Detalles del Servicio
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Categoría:</strong>
                        <Badge color="primary" className="ms-2">{quote.category || 'N/A'}</Badge>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Tipo de Servicio:</strong>
                        <span className="text-muted ms-2">{quote.service_type || fac.service_type || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Horas Totales:</strong>
                        <Badge color="primary" className="ms-2">{fac.total_hours || 'N/A'} hrs</Badge>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Sesiones:</strong>
                        <span className="text-muted ms-2">{fac.sessions || 'N/A'}</span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Participantes:</strong>
                        <span className="text-muted ms-2">{fac.participants || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Grupos:</strong>
                        <span className="text-muted ms-2">{fac.groups || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Idioma:</strong>
                        <span className="text-muted ms-2">{fac.language || 'N/A'}</span>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* 4. LOGÍSTICA */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-delivery-fast me-2"></i>
                    Logística y Entrega
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Fecha de Impartición:</strong>
                        <span className="text-muted ms-2">
                          {fac.service_delivery_date
                            ? parseDate(fac.service_delivery_date).toLocaleDateString('es-MX')
                            : 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Lugar:</strong>
                        <span className="text-muted ms-2">{fac.location || 'N/A'}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Ciudad:</strong>
                        <span className="text-muted ms-2">{fac.city || 'N/A'}</span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Taller:</strong>
                        <span className="text-muted ms-2">{fac.workshop_name || 'N/A'}</span>
                      </p>
                      {fac.facilitator_name && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Facilitador:</strong>
                          <Badge color="info" className="ms-2">{fac.facilitator_name}</Badge>
                        </p>
                      )}
                      {fac.facilitator_id && !fac.facilitator_name && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Facilitador ID:</strong>
                          <span className="text-muted ms-2">{fac.facilitator_id}</span>
                        </p>
                      )}
                    </Col>
                  </Row>
                </div>

                {/* 5. NECESIDAD DEL CLIENTE */}
                {fac.client_need && (
                  <>
                    <hr className="horizontal dark" />
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-bulb-61 me-2"></i>
                        Necesidad del Cliente
                      </h6>
                      <div className="bg-gradient-light p-4 border-radius-lg shadow-sm">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {fac.client_need}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* 6. DESCRIPCIÓN */}
                {fac.description && (
                  <>
                    <hr className="horizontal dark" />
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-align-left-2 me-2"></i>
                        Descripción del Servicio
                      </h6>
                      <div className="bg-light p-3 border-radius-lg">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {fac.description}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* 7. NOTAS */}
                {fac.notes && (
                  <>
                    <hr className="horizontal dark" />
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-align-left-2 me-2"></i>
                        Notas
                      </h6>
                      <div className="bg-light p-3 border-radius-lg">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {fac.notes}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Sidebar */}
          <Col lg="4">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-folder-17 me-2"></i>
                  Archivos
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                {quote.pdf_url ? (
                  <Button
                    color="primary"
                    size="sm"
                    block
                    className="btn-icon shadow-sm mb-2"
                    onClick={() => window.open(quote.pdf_url, '_blank')}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-single-copy-04"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Ver PDF Propuesta</span>
                  </Button>
                ) : (
                  <div className="text-center py-3">
                    <i className="ni ni-folder-17 text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-xs text-muted mb-0 mt-2">No hay PDF disponible</p>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Estado */}
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-badge me-2"></i>
                  Estado
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                <div className="text-center">
                  <Badge color="warning" className="badge-lg p-2 mb-3">
                    <i className="ni ni-time-alarm me-1"></i>
                    Pendiente de OC
                  </Badge>
                  <p className="text-xs text-muted">
                    Esta cotización fue aceptada sin orden de compra formal.
                    Los datos de facilitación se integrarán automáticamente cuando se reciba la OC.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          .sidebar, .navbar, .btn, .badge, .card-header .bg-gradient-primary,
          .min-height-300, .col-lg-4 .card { display: none !important; }
          .container-fluid { max-width: 100%; padding: 0; margin: 0; }
          .printable-content { display: block !important; width: 100% !important; }
          .col-lg-8 { width: 100% !important; max-width: 100% !important; }
          .card { border: 1px solid #dee2e6 !important; box-shadow: none !important; }
          body { font-size: 11pt; color: #000; background: #fff; }
          @page { margin: 2cm; }
        }
      `}</style>
    </>
  );
}

export default PendingFacilitationDetail;
